/**
 * Federated ⌘K search — registry-driven, never hand-wired (#9).
 *
 * Every module already registers a governed `search_<entity>(p_query, p_limit)`
 * RPC and a `core.register_mcp_tool` row for it (the tenders 0002 house
 * pattern). Those rows ARE the discovery mechanism: the palette reads
 * `describe_tools()` (entitlement-filtered server-side), keeps the read-only
 * rpc-bound tools that declare a `query` input, and fans the user's query out
 * to all of them in parallel. A new module's search tool therefore appears in
 * ⌘K with ZERO console changes — the alternative, a hardcoded entity list, is
 * exactly what this replaces (it knew about 3 of the 34 registered tools).
 *
 * Authorization is untouched by design: every search_* function is
 * `security definer` but gates each row on `core.can_see` (which since core
 * 0119 genuinely means visibility), and `describe_tools()` already withholds
 * unentitled modules — so the fan-out can never widen what a caller sees.
 *
 * Ranking: each tool returns its rows best-first (core.text_rank, core 0083 —
 * word-order + stemming aware, with an ILIKE floor for mid-word typing).
 * Server order is preserved WITHIN a group; groups are ordered by how well
 * their best hit's label matches the query. When the embeddings ranker lands
 * (#9's semantic half) it replaces text_rank server-side and nothing here
 * changes — the contract is the tool row, not the ranking implementation.
 *
 * Latency: one budget per tool (default 800 ms), all tools in parallel, so the
 * palette answers within one budget regardless of how many modules are
 * installed; stragglers are dropped and counted, never awaited.
 */
import type { EntityTypeInfo, McpToolSpec, Row, SolumClient } from '@solum/sdk'
import { unnamedRecordTitle } from '../data/recordIdentity'

/** A registered search tool the palette can fan out to. */
export interface SearchTool {
  /** the registered (global) tool name, e.g. 'search_tender' / 'cpq_search_quote' */
  name: string
  module: string
  /** the rpc bind — where the query actually goes */
  schema: string
  fn: string
  /** the bind's arg mapping (tool input -> RPC parameter), interpreted like the SDK's bind interpreter */
  args: Record<string, { from?: string; const?: unknown; default?: unknown }>
  /** the metamodel entity a hit navigates to — resolved, never guessed blind; null ⇒ hit is shown but not navigable */
  entity: string | null
  /** group heading — the entity's label when resolved, a humanized fn suffix otherwise */
  label: string
}

/** One row a tool returned, shaped for display without knowing its columns. */
export interface SearchHit {
  id: string
  label: string
  detail: string | null
}

export interface SearchGroup {
  tool: SearchTool
  hits: SearchHit[]
  tookMs: number
}

export interface FederatedResult {
  groups: SearchGroup[]
  /** how many tools were asked */
  sources: number
  /** tools that blew the latency budget (dropped, not awaited) */
  dropped: number
  /** tools that errored (a refusal is not a palette failure) */
  failed: number
}

/* ── discovery ──────────────────────────────────────────────────────────── */

type RpcBind = { kind?: string; schema?: string; fn?: string; entity?: string; args?: Record<string, { from?: string; const?: unknown; default?: unknown }> }

const depluralize = (w: string): string =>
  w.endsWith('ies') ? `${w.slice(0, -3)}y` : w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w

/**
 * Map a tool to the entity its hits belong to, validated against the metamodel.
 * The bind MAY declare it outright (`bind.entity`) — the explicit convention for
 * tools whose fn name doesn't spell the entity (search_instances, search_kb…).
 * Otherwise: `<module>_<fn suffix>` exact, then depluralized, then as a UNIQUE
 * key prefix (search_kb → service_kb_article). Unresolvable ⇒ null — the hits
 * still render, they just can't navigate.
 */
export function resolveEntityKey(module: string, fn: string, hint: string | undefined, keys: ReadonlySet<string>): string | null {
  if (hint && keys.has(hint)) return hint
  const suffix = fn.replace(/^.*?search_/, '')
  if (!suffix) return null
  const singular = depluralize(suffix)
  for (const cand of [`${module}_${suffix}`, `${module}_${singular}`]) if (keys.has(cand)) return cand
  const prefixed = [...keys].filter((k) => k.startsWith(`${module}_${singular}`))
  return prefixed.length === 1 ? prefixed[0]! : null
}

const humanize = (s: string): string => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/**
 * The searchable subset of the registry: read-only rpc-bound tools that call a
 * search function and declare a free-text `query` input. Matching on the
 * CONTRACT (bind + input schema) rather than a curated list is the point — the
 * registry row a module migration writes is all it takes to appear here.
 */
export function deriveSearchTools(specs: McpToolSpec[], entities: EntityTypeInfo[]): SearchTool[] {
  const keys = new Set(entities.map((e) => e.key))
  const labelOf = new Map(entities.map((e) => [e.key, e.label]))
  const out: SearchTool[] = []
  for (const spec of specs) {
    const bind = spec.bind as RpcBind
    if (spec.mutates || bind.kind !== 'rpc' || !bind.schema || !bind.fn) continue
    if (!/(^|_)search_/.test(`_${bind.fn}`) && !/(^|_)search_/.test(`_${spec.name}`)) continue
    const props = (spec.inputSchema as { properties?: Record<string, unknown> }).properties ?? {}
    if (!('query' in props)) continue
    const entity = resolveEntityKey(spec.module, bind.fn, bind.entity, keys)
    out.push({
      name: spec.name,
      module: spec.module,
      schema: bind.schema,
      fn: bind.fn,
      args: bind.args ?? { p_query: { from: 'query' }, p_limit: { from: 'limit' } },
      entity,
      label: (entity && labelOf.get(entity)) || humanize(bind.fn.replace(/^.*?search_/, '')),
    })
  }
  return out
}

/* ── execution ──────────────────────────────────────────────────────────── */

/** Build the RPC payload from {query, limit} through the bind's own arg mapping. */
export function buildRpcArgs(tool: SearchTool, query: string, limit: number): Row {
  const input: Record<string, unknown> = { query, limit }
  const out: Row = {}
  for (const [key, s] of Object.entries(tool.args)) {
    if ('const' in s) { out[key] = s.const ?? null; continue }
    const v = s.from !== undefined ? input[s.from] : undefined
    if (v !== undefined) out[key] = v
    else if ('default' in s) out[key] = s.default
  }
  return out
}

/**
 * Shape an unknown result row for display: id + the first text column + the rest.
 *
 * ── THE LABEL IS NEVER THE UUID (#1705, fixed here #2351) ─────────────────
 * This read `?? id`: a door returning a row whose text columns are all empty —
 * an organization saved with neither a legal nor a display name, a contact
 * point with neither a label nor a value — put the record's uuid on screen as
 * its name, in ⌘K and in every relationship picker that searches through a
 * registered door. `recordIdentity.ts` is where that ruling is enforceable and
 * says so in as many words: *"The uuid. Not as a label, not as a fallback, not
 * as a title attribute."*
 *
 * `typeLabel` is the entity's governed SINGULAR label when the caller holds
 * one, which turns the fallback into `Unnamed organization`. A caller that does
 * not — ⌘K's tool label is a plural group heading, and `Unnamed people` is
 * worse than no noun — passes nothing and gets `Unnamed record`. The id still
 * travels as `id`, so the hit remains selectable and navigable; it just is not
 * what anybody reads.
 */
export function rowsToHits(rows: Row[], typeLabel?: string | null): SearchHit[] {
  const hits: SearchHit[] = []
  for (const row of rows) {
    const id = row.id == null ? null : String(row.id)
    if (!id) continue
    const cols = Object.entries(row).filter(([k]) => k !== 'id')
    const labelEntry = cols.find(([, v]) => typeof v === 'string' && v.trim() !== '')
    const label = (labelEntry?.[1] as string | undefined) ?? unnamedRecordTitle(typeLabel)
    const detail = cols
      .filter(([k, v]) => k !== labelEntry?.[0] && (typeof v === 'string' || typeof v === 'number') && String(v).trim() !== '')
      .map(([, v]) => String(v))
      .join(' · ')
      .slice(0, 80)
    hits.push({ id, label, detail: detail || null })
  }
  return hits
}

/**
 * How well a hit's label matches the query — used ONLY to order groups against
 * each other (within a group the server's rank order is the truth). 3 = exact,
 * 2 = prefix / all tokens at word starts, 1 = all tokens as substrings, 0 = the
 * match is elsewhere (body/snippet) — still shown, ranked below label hits.
 */
export function scoreLabel(query: string, label: string): number {
  const q = query.trim().toLowerCase()
  const l = label.toLowerCase()
  if (!q) return 0
  if (l === q) return 3
  const tokens = q.split(/\s+/).filter(Boolean)
  const escape = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (l.startsWith(q) || tokens.every((t) => new RegExp(`(^|[^\\p{L}\\p{N}])${escape(t)}`, 'u').test(l))) return 2
  if (tokens.every((t) => l.includes(t))) return 1
  return 0
}

class SearchTimeout extends Error {
  constructor() { super('search tool exceeded its latency budget') }
}

function withBudget<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SearchTimeout()), ms)
    p.then((v) => { clearTimeout(timer); resolve(v) }, (e: unknown) => { clearTimeout(timer); reject(e as Error) })
  })
}

export const TOOL_BUDGET_MS = 800
export const PER_TOOL_LIMIT = 8

/**
 * Fan the query out to every discovered tool in parallel and merge, grouped per
 * entity. Failures and stragglers cost their group, never the palette: a module
 * whose search refuses or times out is counted and dropped while every other
 * group still answers.
 */
export async function runFederatedSearch(
  client: SolumClient,
  tools: readonly SearchTool[],
  query: string,
  opts: { limit?: number; budgetMs?: number } = {},
): Promise<FederatedResult> {
  const limit = opts.limit ?? PER_TOOL_LIMIT
  const budgetMs = opts.budgetMs ?? TOOL_BUDGET_MS
  const raw = client.raw()
  const results = await Promise.all(tools.map(async (tool, order) => {
    const t0 = Date.now()
    try {
      const rows = await withBudget(raw.rpcTable<Row>(tool.fn, buildRpcArgs(tool, query, limit), tool.schema), budgetMs)
      return { tool, order, hits: rowsToHits(Array.isArray(rows) ? rows : []), tookMs: Date.now() - t0, timedOut: false, failed: false }
    } catch (e) {
      return { tool, order, hits: [], tookMs: Date.now() - t0, timedOut: e instanceof SearchTimeout, failed: !(e instanceof SearchTimeout) }
    }
  }))
  const groups = results
    .filter((r) => !r.timedOut && !r.failed && r.hits.length > 0)
    .sort((a, b) => {
      const sa = Math.max(...a.hits.map((h) => scoreLabel(query, h.label)))
      const sb = Math.max(...b.hits.map((h) => scoreLabel(query, h.label)))
      return sb - sa || a.order - b.order
    })
    .map(({ tool, hits, tookMs }) => ({ tool, hits, tookMs }))
  return {
    groups,
    sources: tools.length,
    dropped: results.filter((r) => r.timedOut).length,
    failed: results.filter((r) => r.failed).length,
  }
}
